import { createSelector } from "reselect";

import type { CrmIntegrationProvider } from "app/pages/sales-intelligence/domain/crm-integration/types/provider";
import type { RootState } from "single-spa/store/types";

import {
    selectHsIsNotIntegrated,
    selectHubspotLoadingState,
    selectIsHsCrmLabelEnabled,
    selectIsHsCrmListEnabled,
    selectIsHubspotLeadsFilterEnabled,
} from "./hubspotSelectors";
import {
    selectIsSalesforceLeadsFilterEnabled,
    selectIsSalesForceNotIntegrated,
    selectIsSfCrmLabelEnabled,
    selectIsSfCrmListEnabled,
    selectSalesforceLoadingState,
} from "./salesforceSelectors";
import {
    selectDynamicsIsNotIntegrated,
    selectDynamicsLoadingState,
    selectIsDynamicsCrmLabelEnabled,
    selectIsDynamicsCrmListEnabled,
    selectIsDynamicsLeadsFilterEnabled,
} from "./dynamicsSelectors";

export const selectIsCrmLabelEnabled = createSelector(
    [selectIsSfCrmLabelEnabled, selectIsHsCrmLabelEnabled, selectIsDynamicsCrmLabelEnabled, (_state: RootState, crm: CrmIntegrationProvider) => crm],
    (isSfLabelsEnabled, isHsLabelsEnabled, isDynamicsLabelsEnabled, crm) => {
        return {
            salesforce: isSfLabelsEnabled,
            hubspot: isHsLabelsEnabled,
            dynamics: isDynamicsLabelsEnabled
        }[crm] ?? false;
    },
);

export const selectIsCrmListEnabled = createSelector(
    [selectIsSfCrmListEnabled, selectIsHsCrmListEnabled, selectIsDynamicsCrmListEnabled, (_state: RootState, crm?: CrmIntegrationProvider) => crm],
    (isSfListEnabled, isHsListEnabled, isDynamicsListEnabled, crm) => {
        return {
            salesforce: isSfListEnabled,
            hubspot: isHsListEnabled,
            dynamics: isDynamicsListEnabled
        }[crm as CrmIntegrationProvider] ?? false;
    },
);

export const selectIsCrmNotIntegrated = createSelector(
    [
        selectIsSalesForceNotIntegrated,
        selectHsIsNotIntegrated,
        selectDynamicsIsNotIntegrated,
        (_state: RootState, crm?: CrmIntegrationProvider) => crm,
    ],
    (isSfNotIntegrated, isHsNotIntegrated, isDynamicsNotIntegrated, crm) => {
        return {
            salesforce: isSfNotIntegrated,
            hubspot: isHsNotIntegrated,
            dynamics: isDynamicsNotIntegrated
        }[crm as CrmIntegrationProvider] ?? true;
    },
);

export const selectIsCrmStatusLoading = createSelector(
    [selectSalesforceLoadingState, selectHubspotLoadingState, selectDynamicsLoadingState, (_state: RootState, crm?: CrmIntegrationProvider) => crm],
    (isSfLoading, isHsLoading, isDynamicsLoading, crm) => {
        return {
            salesforce: isSfLoading,
            hubspot: isHsLoading,
            dynamics: isDynamicsLoading
        }[crm as CrmIntegrationProvider] ?? false;
    },
);

export const selectIsCrmFilterEnabled = createSelector(
    [
        selectIsSalesforceLeadsFilterEnabled,
        selectIsHubspotLeadsFilterEnabled,
        selectIsDynamicsLeadsFilterEnabled,
        (_state: RootState, crm: CrmIntegrationProvider) => crm,
    ],
    (isSfFilterEnabled, isHsFilterEnabled, isDynamicsFilterEnabled, crm) => {
        return {
            salesforce: isSfFilterEnabled,
            hubspot: isHsFilterEnabled,
            dynamics: isDynamicsFilterEnabled
        }[crm] ?? false;
    },
);
